// DeleteMediaModal — EW-010 HONEST deletion (WL-023) + WL-072 checklist.
// This modal makes deletion honest to the user: when any selected asset is still
// referenced by ≥1 Wheel, the delete is surfaced as DESTRUCTIVE ("removes it
// everywhere it is used") rather than silent. WL-072 adds the alternative: for a
// SINGLE file with a known (`tracked`) Wheel list, a checkable list of exactly
// those Wheels lets the user detach the file from just the ones they pick instead
// of deleting it from the Library outright.
//
// media-delete-modal-ux cluster (Gordon, ISP-9999): a file that IS the user's
// own profile picture can't be deleted at all (the BE's own guard 409s it) —
// that's a HARD block, distinct from the soft "still in use on a Wheel"
// warning above. blockReason() is derived from props alone, so it's known and
// rendered the instant the modal opens, never only after a failed click.
import React, { useState } from "react";
import PropTypes from "prop-types";
import * as S from "./MediaLibrary.styled";
import { IconClose, IconTrash } from "./icons";
import { refcountLabel, isDeletionDestructive, usageState, blockReason } from "../../pages/MediaLibrary/helpers";
import ChangeProfilePictureModal from "./ChangeProfilePictureModal";

export default function DeleteMediaModal({
  open = true,
  items = [],
  busy = false,
  error = null,
  profileImageUrl = null,
  onCancel,
  onConfirm,
  onProfilePictureChanged,
  wheelsLoading = false,
  selectedWheelIds = [],
  onToggleWheel,
  removeBusy = false,
  removeError = null,
  onRemoveFromWheels,
}) {
  const [showChangeProfilePicture, setShowChangeProfilePicture] = useState(false);
  if (!open) return null;
  const count = items.length;
  // Hard block: computed on every render from props alone — no click required.
  const blockedEntries = items
    .map((i) => ({ item: i, reason: blockReason(i, profileImageUrl) }))
    .filter((e) => e.reason);
  const blocked = blockedEntries.map((e) => e.item);
  const isBlocked = blocked.length > 0;
  const onlyProfilePictureBlock = isBlocked && blockedEntries.every((e) => e.reason === "profile-picture");
  // EW-1283: three honest states, never conflate "unknown" with "in use".
  const inUse = items.filter((i) => isDeletionDestructive(i.usedInWheels)); // confirmed refcount > 0
  const unknown = items.filter((i) => usageState(i.usedInWheels) === "unknown"); // BE refcount not shipped (WL-072)
  const destructive = inUse.length > 0;
  const uncertain = !destructive && unknown.length > 0;
  const busyAny = busy || removeBusy;

  // WL-072: the checklist only makes sense for a SINGLE file with a known
  // (non-empty, tracked) Wheel list — a multi-file checklist across N files x M
  // Wheels is out of scope here; those deletes keep the existing honest
  // destructive/uncertain/safe messaging above.
  const singleItem = count === 1 ? items[0] : null;
  const wheelChoices = Array.isArray(singleItem?.wheels) ? singleItem.wheels : [];
  const showWheelChecklist = Boolean(singleItem) && wheelChoices.length > 0;

  return (
    <S.Backdrop role="dialog" aria-modal="true" aria-label="Delete media" data-testid="delete-media-modal" onClick={(e) => e.target === e.currentTarget && !busyAny && onCancel && onCancel()}>
      <S.Modal w={520}>
        <S.ModalHead>
          <div>
            <div className="t">{count > 1 ? `Delete ${count} items?` : "Delete this item?"}</div>
          </div>
          <button type="button" className="x" aria-label="Close" onClick={onCancel}><IconClose size={20} /></button>
        </S.ModalHead>
        <S.ModalBody>
          {isBlocked ? (
            <div data-testid="delete-blocked-warning" style={{ background: "#FFF4F4", border: "1px solid #F3C7C7", borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#B93636", marginBottom: 6 }}>
                {blocked.length === count ? "Can't delete" : `${blocked.length} of ${count} can't be deleted`}
              </div>
              <div style={{ fontSize: 14, color: "#565556", lineHeight: 1.5 }}>
                {count > 1 ? "These files are" : "This file is"} your profile picture. Change your profile picture first, then you can delete {count > 1 ? "them" : "it"}.
              </div>
              {onlyProfilePictureBlock && (
                <button
                  type="button"
                  data-testid="delete-modal-change-profile-picture"
                  onClick={() => setShowChangeProfilePicture(true)}
                  style={{
                    marginTop: 10,
                    background: "none",
                    border: "1px solid #123051",
                    borderRadius: 20,
                    padding: "8px 16px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#123051",
                    cursor: "pointer",
                  }}
                >
                  Change profile picture
                </button>
              )}
            </div>
          ) : destructive ? (
            <div data-testid="delete-inuse-warning" style={{ background: "#FFF4F4", border: "1px solid #F3C7C7", borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#B93636", marginBottom: 6 }}>
                {inUse.length === count ? "Still in use" : `${inUse.length} of ${count} still in use`}
              </div>
              <div style={{ fontSize: 14, color: "#565556", lineHeight: 1.5 }}>
                Deleting removes {count > 1 ? "these files" : "this file"} from your Library and from
                every Wheel that uses {count > 1 ? "them" : "it"}. This can't be undone.
              </div>
              <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
                {inUse.slice(0, 4).map((i) => (
                  <li key={i.id} style={{ fontSize: 13, color: "#123051", display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.name}</span>
                    <span style={{ color: "#8F38EF", fontWeight: 600, flex: "none" }}>{refcountLabel(i.usedInWheels)}</span>
                  </li>
                ))}
                {inUse.length > 4 && <li style={{ fontSize: 13, color: "#9AA0A6" }}>+{inUse.length - 4} more</li>}
              </ul>
            </div>
          ) : uncertain ? (
            // EW-1283: usage is UNKNOWN (BE refcount WL-072 not shipped). Be honest —
            // do NOT claim "still in use", and do NOT claim it's unused either.
            <div data-testid="delete-unknown-usage" style={{ background: "#F6F7F9", border: "1px solid #E3E6EA", borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#123051", marginBottom: 6 }}>
                Usage not tracked yet
              </div>
              <div style={{ fontSize: 14, color: "#565556", lineHeight: 1.5 }}>
                Wheel-usage check coming soon — deleting is permanent and cannot be undone.
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 14, color: "#565556", lineHeight: 1.5 }}>
              {count > 1 ? "These files aren't used on any Wheel." : "This file isn't used on any Wheel."} Deleting
              permanently removes {count > 1 ? "them" : "it"} from your Library.
            </div>
          )}
          {wheelsLoading ? (
            <div data-testid="wheels-checklist-loading" style={{ marginTop: 12, fontSize: 13, color: "#9AA0A6" }}>
              Checking which Wheels use this file…
            </div>
          ) : null}
          {showWheelChecklist ? (
            <div data-testid="delete-wheels-checklist" style={{ marginTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#123051", marginBottom: 8 }}>
                Or remove it from specific Wheels instead of deleting
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                {wheelChoices.map((w) => (
                  <li key={w.id}>
                    <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: busyAny ? "default" : "pointer" }}>
                      <input
                        type="checkbox"
                        data-testid={`wheel-checkbox-${w.id}`}
                        checked={selectedWheelIds.includes(w.id)}
                        disabled={busyAny}
                        onChange={() => onToggleWheel && onToggleWheel(w.id)}
                      />
                      {w.thumbnail ? (
                        <img src={w.thumbnail} alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: "cover", flex: "none" }} />
                      ) : (
                        <div aria-hidden="true" style={{ width: 28, height: 28, borderRadius: 4, background: "#E3E6EA", flex: "none" }} />
                      )}
                      <span style={{ fontSize: 13, color: "#123051", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
              {removeError ? (
                <div data-testid="remove-from-wheels-error" role="alert" style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: "#B93636" }}>
                  {removeError}
                </div>
              ) : null}
              <S.GhostButton
                type="button"
                data-testid="remove-from-wheels-button"
                disabled={busyAny || selectedWheelIds.length === 0}
                aria-busy={removeBusy}
                style={{ marginTop: 10 }}
                onClick={() => !busyAny && onRemoveFromWheels && onRemoveFromWheels(singleItem)}
              >
                {removeBusy
                  ? "Removing…"
                  : `Remove from selected Wheels${selectedWheelIds.length ? ` (${selectedWheelIds.length})` : ""}`}
              </S.GhostButton>
            </div>
          ) : null}
          {error ? (
            <div data-testid="delete-error" role="alert" style={{ marginTop: 12, fontSize: 13, fontWeight: 600, color: "#B93636" }}>
              {error}
            </div>
          ) : null}
        </S.ModalBody>
        <S.ModalFoot>
          <S.GhostButton onClick={onCancel} disabled={busyAny}>Cancel</S.GhostButton>
          <S.PrimaryButton
            danger
            disabled={busyAny || isBlocked}
            aria-busy={busy}
            onClick={() => !busyAny && !isBlocked && onConfirm && onConfirm(items)}
          >
            {!busy && <IconTrash size={15} data-testid="delete-modal-trash-icon" />}
            {busy ? "Deleting…" : destructive ? "Delete everywhere" : "Delete"}
          </S.PrimaryButton>
        </S.ModalFoot>
      </S.Modal>
      {showChangeProfilePicture && (
        <ChangeProfilePictureModal
          open
          onClose={() => setShowChangeProfilePicture(false)}
          onSaved={() => {
            setShowChangeProfilePicture(false);
            onProfilePictureChanged && onProfilePictureChanged();
          }}
        />
      )}
    </S.Backdrop>
  );
}

DeleteMediaModal.propTypes = {
  open: PropTypes.bool,
  items: PropTypes.array,
  busy: PropTypes.bool,
  error: PropTypes.string,
  profileImageUrl: PropTypes.string,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  onProfilePictureChanged: PropTypes.func,
  wheelsLoading: PropTypes.bool,
  selectedWheelIds: PropTypes.array,
  onToggleWheel: PropTypes.func,
  removeBusy: PropTypes.bool,
  removeError: PropTypes.string,
  onRemoveFromWheels: PropTypes.func,
};
